import {
  ChangeEvent,
  ComponentProps,
  KeyboardEventHandler,
  useState,
} from "react";
import { useTheme } from "@material-ui/core/styles";
import classnames from "classnames";

import useStyles from "./styles";
import { Box, Chip, Input, InputHelpText } from "..";

interface Props extends Omit<ComponentProps<typeof Input>, "onBlur" | "error"> {
  data: string[];
  validate?: (value: string) => boolean;
  onAdd: (chip: string) => void;
  onBlur: (chip: string) => void;
  onDelete: (chip: string) => void;
  placeholder: string;
  error?: string | boolean | JSX.Element;
  showRemainingChars?: undefined;
}

const ChipInput = ({
  data,
  onAdd,
  onBlur,
  onDelete,
  placeholder,
  error,
  description,
  validate,
  ...rest
}: Props): JSX.Element => {
  const theme = useTheme();
  const [value, setValue] = useState("");
  const styles = useStyles(rest);
  const handleDelete = (item: string) => () => {
    onDelete(item);
  };

  const invokeCallbackIfValueIsNotDuplicate = (
    callback: (chip: string) => void,
  ) => {
    if (!value) return;

    const isValueUnique = !data.includes(value);
    if (isValueUnique) {
      callback(value);
    }
    setValue("");
  };

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (["Enter", "Tab", ","].includes(e.key)) {
      e.preventDefault();

      invokeCallbackIfValueIsNotDuplicate(onAdd);
    }
  };

  const handleBlur = () => {
    invokeCallbackIfValueIsNotDuplicate(onBlur);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  };

  return (
    <>
      <Box
        border={1}
        borderColor={error ? theme.palette.red.main : theme.palette.gray.light}
        borderRadius={5}
        className={styles.chipsContainer}
      >
        {data.map((datum) => (
          <Chip
            data-testid="chip"
            className={classnames(styles.chips, {
              [styles.error]: validate ? !validate(datum) : false,
            })}
            key={datum}
            tabIndex={-1}
            label={datum}
            onDelete={handleDelete(datum)}
          />
        ))}
        <Input
          value={value}
          size="sm"
          inputProps={{
            onKeyDown: handleKeyDown,
            "data-testid": "chip-input",
          }}
          fullWidth
          className={styles.input}
          placeholder={placeholder}
          onBlur={handleBlur}
          onChange={handleChange}
          {...rest}
        />
      </Box>
      <InputHelpText isError={Boolean(error)}>
        {error || description}
      </InputHelpText>
    </>
  );
};

export default ChipInput;
